import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import ModalComponent from '@/components/molecules/ModalComponent/ModalComponent';
import { parseLocale } from '@/utils/localeFormatters';

import type { DiffSummary } from '../form/formDiff';

import { summaryParts } from './saveSummary';

import styles from './modals.module.scss';

export interface SaveConfirmModalProps {
    isOpen: boolean;
    summary: DiffSummary;
    saving: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /**
     * After the dialog has closed and handed focus back to what opened it, so a caller that
     * wants focus elsewhere moves it from there.
     */
    onClosed: () => void;
}

function SaveConfirmModal({
    isOpen,
    summary,
    saving,
    onConfirm,
    onCancel,
    onClosed,
}: SaveConfirmModalProps) {
    const { t, i18n } = useTranslation();
    const list = new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' });
    const changes = list.format(
        summaryParts(summary).map((part) =>
            part.count === undefined ? t(part.key) : t(part.key, { count: part.count })
        )
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={saving ? () => {} : onCancel}
            backdrop={saving ? 'static' : true}
            keyboard={!saving}
            onClosed={onClosed}
            title={t('providerConfig.save.confirmTitle')}
            body={<p className={styles.text}>{t('providerConfig.save.summary', { changes })}</p>}
            footer={
                <>
                    <Button variant="secondary" disabled={saving} onClick={onCancel}>
                        {t('providerConfig.save.cancel')}
                    </Button>
                    <Button variant="primary" disabled={saving} onClick={onConfirm}>
                        {t('providerConfig.save.confirm')}
                    </Button>
                </>
            }
        />
    );
}

export default SaveConfirmModal;
export { SaveConfirmModal };
